import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import {
  loadCart,
  saveCart,
  normalizeCartItem,
  MAX_CART_ITEM_QUANTITY,
  DEFAULT_CART_ITEM_QUANTITY,
  type CartItem,
} from "@/lib/cartStorage";
import { MAX_CART_ITEMS } from "@/lib/whatsapp";

export type { CartItem };

interface CartState {
  storeSlug: string | null;
  storeId: string | null;
  items: CartItem[];
  isReady: boolean;
  pendingValidation: CartItem[] | null;
}

type CartAction =
  | { type: "SET_STORE"; storeSlug: string; storeId: string }
  | { type: "HYDRATE_CART"; items: CartItem[] }
  | { type: "ADD_ITEM"; item: Omit<CartItem, "quantity"> & { quantity?: number } }
  | { type: "UPDATE_QUANTITY"; productId: string; quantity: number }
  | { type: "REMOVE_ITEM"; productId: string }
  | { type: "CLEAR" };

function cartLineCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

function cartTotalCents(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
}

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "SET_STORE":
      if (state.storeSlug === action.storeSlug && state.storeId === action.storeId) {
        return state;
      }
      {
        const persisted = loadCart(action.storeSlug);
        if (persisted.length === 0) {
          return {
            storeSlug: action.storeSlug,
            storeId: action.storeId,
            items: [],
            isReady: true,
            pendingValidation: null,
          };
        }
        return {
          storeSlug: action.storeSlug,
          storeId: action.storeId,
          items: [],
          isReady: false,
          pendingValidation: persisted,
        };
      }
    case "HYDRATE_CART":
      return {
        ...state,
        items: action.items.map(normalizeCartItem),
        isReady: true,
        pendingValidation: null,
      };
    case "ADD_ITEM": {
      if (!state.isReady) return state;

      const incoming = normalizeCartItem({
        ...action.item,
        quantity: action.item.quantity ?? DEFAULT_CART_ITEM_QUANTITY,
      });
      const existingIndex = state.items.findIndex((i) => i.productId === incoming.productId);

      if (existingIndex >= 0) {
        const existing = state.items[existingIndex]!;
        if (existing.quantity >= MAX_CART_ITEM_QUANTITY) return state;

        const nextItems = [...state.items];
        nextItems[existingIndex] = normalizeCartItem({
          ...incoming,
          quantity: existing.quantity + 1,
        });
        return { ...state, items: nextItems };
      }

      if (state.items.length >= MAX_CART_ITEMS) return state;
      return { ...state, items: [...state.items, incoming] };
    }
    case "UPDATE_QUANTITY": {
      if (!state.isReady) return state;

      if (action.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((i) => i.productId !== action.productId),
        };
      }

      const capped = Math.min(MAX_CART_ITEM_QUANTITY, Math.floor(action.quantity));
      return {
        ...state,
        items: state.items.map((item) =>
          item.productId === action.productId ? { ...item, quantity: capped } : item
        ),
      };
    }
    case "REMOVE_ITEM":
      if (!state.isReady) return state;
      return {
        ...state,
        items: state.items.filter((i) => i.productId !== action.productId),
      };
    case "CLEAR":
      if (!state.isReady) return state;
      return { ...state, items: [] };
    default:
      return state;
  }
}

interface CartContextValue {
  storeId: string | null;
  items: CartItem[];
  itemCount: number;
  totalCents: number;
  isReady: boolean;
  isHydrating: boolean;
  pendingProductIds: string[];
  pendingValidation: CartItem[] | null;
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  canAddMore: boolean;
  hasItem: (productId: string) => boolean;
  getItemQuantity: (productId: string) => number;
  syncStore: (storeSlug: string, storeId: string) => void;
  hydrateCart: (items: CartItem[]) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

const initialState: CartState = {
  storeSlug: null,
  storeId: null,
  items: [],
  isReady: false,
  pendingValidation: null,
};

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  useEffect(() => {
    if (state.storeSlug && state.isReady) {
      saveCart(state.storeSlug, state.items);
    }
  }, [state.storeSlug, state.items, state.isReady]);

  const syncStore = useCallback((storeSlug: string, storeId: string) => {
    dispatch({ type: "SET_STORE", storeSlug, storeId });
  }, []);

  const hydrateCart = useCallback((items: CartItem[]) => {
    dispatch({ type: "HYDRATE_CART", items });
  }, []);

  const addItem = useCallback((item: Omit<CartItem, "quantity"> & { quantity?: number }) => {
    dispatch({ type: "ADD_ITEM", item });
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    dispatch({ type: "UPDATE_QUANTITY", productId, quantity });
  }, []);

  const removeItem = useCallback((productId: string) => {
    dispatch({ type: "REMOVE_ITEM", productId });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR" });
  }, []);

  const hasItem = useCallback(
    (productId: string) => state.items.some((i) => i.productId === productId),
    [state.items]
  );

  const getItemQuantity = useCallback(
    (productId: string) =>
      state.items.find((i) => i.productId === productId)?.quantity ?? 0,
    [state.items]
  );

  const value = useMemo<CartContextValue>(
    () => ({
      storeId: state.storeId,
      items: state.items,
      itemCount: cartLineCount(state.items),
      totalCents: cartTotalCents(state.items),
      isReady: state.isReady,
      isHydrating: !state.isReady && state.pendingValidation != null,
      pendingProductIds: state.pendingValidation?.map((i) => i.productId) ?? [],
      pendingValidation: state.pendingValidation,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      canAddMore: state.isReady && state.items.length < MAX_CART_ITEMS,
      hasItem,
      getItemQuantity,
      syncStore,
      hydrateCart,
    }),
    [
      state.storeId,
      state.items,
      state.isReady,
      state.pendingValidation,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      hasItem,
      getItemQuantity,
      syncStore,
      hydrateCart,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
